namespace Portico.Services {

	import pme = Portico.Models.Enterprise;
	import pmg = Portico.Models.Gateway;
	import psga = apigateway.client;
	import psge = apigateway.enums;
	import psgm = apigateway.models;
    import Inject = Portico.Decorators.Inject;
    import Service = Portico.Decorators.Service;

    export interface IPayorService {
		get(id: number): angular.IPromise<pme.Payor>;
		getPayorBenefit(accountId: number, payorId: number, role: psge.PayorType): angular.IPromise<pme.PayorBenefit>;
		getPlanTypes(): angular.IPromise<psgm.PayorRoleTypeModel[]>;
		populate(account: pme.Account): angular.IPromise<boolean>;
        save: (payor: pme.Payor) => angular.IPromise<pme.Payor>;
        search(payor: pme.Payor, coverageAreaState: string, max?: number, local?: boolean, global?: boolean): angular.IPromise<pme.Payor[]>;
    }

    @Service('app', 'payorService')
	@Inject('$q', 'PayorV4Api', 'PayorV4GlobalApi')
    export class PayorService implements IPayorService {
        constructor(
            private $q: angular.IQService,
			private payorV4Api: psga.PayorV4Api,
			private payorV4GlobalApi: psga.PayorV4GlobalApi
        ) { }

		get(id: number): angular.IPromise<pme.Payor> {
			// TODO: wrap in an edge call
			return this.$q.when(this.payorV4Api.payorGetByTenantId(id).then((response) => {
				return new pme.Payor(response.data.data);
			}));
		}

		getPayorBenefit(accountId: number, payorId: number, role: psge.PayorType): angular.IPromise<pme.PayorBenefit> {
			if (role === psge.PayorType.INS) {
				// TODO: wrap in an edge call
				return this.$q.when(this.payorV4Api.accountMedicalGet2(accountId, payorId).then((response) => {
					response.data.data.createDate = <moment.Moment><unknown>response.data.data.createDate?.toDate();
					response.data.data.dateOfBirth = <moment.Moment><unknown>response.data.data.dateOfBirth?.toDate();
					response.data.data.planEffectiveDate = <moment.Moment><unknown>response.data.data.planEffectiveDate?.toDate();
					response.data.data.planExpirationDate = <moment.Moment><unknown>response.data.data.planExpirationDate?.toDate();
					response.data.data.planTerminationDate = <moment.Moment><unknown>response.data.data.planTerminationDate?.toDate();
					return new pme.PayorBenefit(response.data.data);
				}));
			} else if (role === psge.PayorType.PBM) {
				// TODO: wrap in an edge call
				return this.$q.when(this.payorV4Api.accountPharmacyGet2(accountId, payorId).then((response) => {
					response.data.data.createDate = <moment.Moment><unknown>response.data.data.createDate?.toDate();
					response.data.data.dateOfBirth = <moment.Moment><unknown>response.data.data.dateOfBirth?.toDate();
					response.data.data.planEffectiveDate = <moment.Moment><unknown>response.data.data.planEffectiveDate?.toDate();
					response.data.data.planExpirationDate = <moment.Moment><unknown>response.data.data.planExpirationDate?.toDate();
					response.data.data.planTerminationDate = <moment.Moment><unknown>response.data.data.planTerminationDate?.toDate();
					return new pme.PayorBenefit(response.data.data);
				}));
			}
		}

		getPlanTypes(): angular.IPromise<psgm.PayorRoleTypeModel[]> {
			return this.$q.when(this.payorV4Api.planTypeGet().then((response) => {
				return response.data.data;
			}));
		}

		populate(account: pme.Account): angular.IPromise<boolean> {
			const promises: angular.IPromise<void>[] = [];

			for (const relationship of account.payorRelationships) {
				if (!relationship.payor) {
					promises.push(this.get(relationship.payorId).then((payor) => {
						relationship.payor = payor;
					}));

					promises.push(this.getPayorBenefit(account.id, relationship.payorId, relationship.role).then((benefit) => {
						relationship.payorBenefit = benefit;
					}));
				}
			}

			return this.$q.all(promises).then(() => {
				return true;
			});
		}

		save(payor: pme.Payor): angular.IPromise<pme.Payor> {
			const request: DeepPartial<psgm.PayorSaveModel> = {
				payorName: _.get(payor, 'name') || '',
				payorType: <pmg.PayorType><unknown>_.get(payor, 'type') || <pmg.PayorType><unknown>pmg.RoleType.INSURANCEPROVIDER,
				payorBin: _.get(payor, 'bin') || '',
				payorPcn: _.get(payor, 'pcn') || '',
				payorGroup: _.get(payor, 'group') || '',
				isPlaceholder: false
			};

			if (payor.id) {
				// TODO: wrap in an edge call
				return this.$q.when(this.payorV4Api.payorUpdateByTenantId(payor.id, request).then((response) => {
					return new pme.Payor(response.data as any);
				}));
			} else {
				// TODO: wrap in an edge call
				return this.$q.when(this.payorV4Api.payorCreateByTenantId(request).then((response) => {
					payor.id = response.data.data;
					return new pme.Payor(payor);
				}));
			}
        }

		search(payor: pme.Payor, stateCoverageArea: string = '',  max: number = 50, local: boolean = false, global: boolean = true): angular.IPromise<pme.Payor[]> {
			const request: DeepPartial<psgm.PayorSearchModel> = {
				maxRecordCount: max,
				payorType: <pmg.PayorType><unknown>_.get(payor, 'type') || 0,
				payorName: _.get(payor, 'name') || '',
				payorGroup: _.get(payor, 'group') || '',
				payorBin: _.get(payor, 'bin') || '',
				payorPcn: _.get(payor, 'pcn') || '',
				addressOne: _.get(payor, 'addresses[0].address1') || '',
				city: _.get(payor, 'addresses[0].city') || '',
				state: _.get(payor, 'addresses[0].state') || '',
                zip: _.get(payor, 'addresses[0].zip') || '',
                coverageAreaState: stateCoverageArea || '',
				isPlaceholder: false
			};

			const localPromise: angular.IPromise<psgm.PayorSearchResponseModel> = local ?
				// TODO: wrap in an edge call
				this.$q.when(this.payorV4Api.payorSearchByTenantId(request).then(response => { return response.data.data; })) :
				this.$q.when({ payors: [] });

			const globalPromise: angular.IPromise<psgm.PayorSearchResponseModel> = global ?
				// TODO: wrap in an edge call
				this.$q.when(this.payorV4GlobalApi.globalPayorSearchByTenantId(request).then(response => { return response.data.data; })) :
				this.$q.when({ payors: [] });

			return this.$q.all({ lp: localPromise, gp: globalPromise }).then((response) => {
				const payors: pme.Payor[] = [];
				const temp = _.unionWith(response.lp.payors, response.gp.payors, (a, b) => {
					return a.payorName === b.payorName && _.get(a, 'addresses[0].state', '') === _.get(b, 'addresses[0].state', '');
				});

				for (const payor of temp) {
					payors.push(new pme.Payor(payor));
				}

				return payors;
			});
		}
	}
}